"use client";

import type { operations } from "@tamariba/api-client";
import {
  defaultMasteryIntervalDays,
  type MasteryPlan,
  maximumMasteryIntervalDays,
  methodDefaults,
  methodHelp,
  methodLabel,
  methodNames,
  type PracticeMethod,
  parseMasteryPlan,
  parsePracticeMethod,
} from "@tamariba/drills";
import { type FormEvent, useState } from "react";
import { FieldLabel } from "../ui/field-label";
import { DrillError } from "./shared";
import { request } from "./types";

export type PersonalPlan = {
  practiceMethod: PracticeMethod;
  masteryPlan: MasteryPlan;
};
export type PracticePlanChoice =
  operations["getDrillCollectionPracticePlan"]["responses"][200]["content"]["application/json"];

const methodNumbers = (method: PracticeMethod): Record<string, string> =>
  Object.fromEntries(
    Object.entries(method)
      .filter(([key]) => key !== "type")
      .map(([key, value]) => [key, String(value)]),
  );

export function PracticePlanEditor({
  collectionId,
  choice,
  startOnSave = false,
  onSaved,
  onCancel,
}: {
  collectionId: string;
  choice: PracticePlanChoice;
  startOnSave?: boolean;
  onSaved: () => void;
  onCancel: () => void;
}) {
  const initial = choice.selected ?? choice.recommended;
  const [methodType, setMethodType] = useState<PracticeMethod["type"]>(
    initial.practiceMethod.type,
  );
  const [numbers, setNumbers] = useState(() =>
    methodNumbers(initial.practiceMethod),
  );
  const [target, setTarget] = useState(
    String(initial.masteryPlan.intervalsDays.length),
  );
  const [intervals, setIntervals] = useState(() =>
    initial.masteryPlan.intervalsDays.map(String),
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const recommendation = choice.recommended;
  async function save(event: FormEvent) {
    event.preventDefault();
    if (busy) return;
    setError("");
    try {
      const practiceMethod = parsePracticeMethod({
        type: methodType,
        ...Object.fromEntries(
          Object.entries(numbers).map(([key, value]) => [key, Number(value)]),
        ),
      });
      if (
        Number(target) !== intervals.length ||
        intervals.some((days) => days.trim() === "")
      )
        throw new Error("達成目標回数と各回の間隔を入力してください。");
      const masteryPlan = parseMasteryPlan({
        intervalsDays: intervals.map(Number),
      });
      setBusy(true);
      await request(`/api/drill-collections/${collectionId}/practice-plan`, {
        practiceMethod,
        masteryPlan,
      });
      onSaved();
    } catch (error) {
      setError((error as Error).message);
    } finally {
      setBusy(false);
    }
  }
  return (
    <section
      className="drill-panel drill-personal-plan"
      aria-labelledby="personal-plan-heading"
    >
      <h2 id="personal-plan-heading">
        {choice.selected ? "自分の練習方法を変更" : "練習方法を選びましょう"}
      </h2>
      <p>
        このドリル集の各ドリルに適用します。設定は自分だけに保存され、あとから変更できます。
      </p>
      <div className="drill-method-summary">
        <strong>
          {recommendation.source === "creator"
            ? "作成者のおすすめ"
            : recommendation.source === "class"
              ? `${recommendation.className ?? "あなた"}クラスへのおすすめ`
              : "おすすめの練習方法"}
        </strong>
        <span>
          {methodLabel(recommendation.practiceMethod)} ·{" "}
          {recommendation.masteryPlan.intervalsDays.length}回達成
        </span>
        <span>
          間隔：
          {recommendation.masteryPlan.intervalsDays
            .map((days, index) => (index === 0 ? "当日" : `${days}日後`))
            .join(" → ")}
          （前回の達成日から）
        </span>
        <button
          type="button"
          className="secondary-button"
          disabled={busy}
          onClick={() => {
            setMethodType(recommendation.practiceMethod.type);
            setNumbers(methodNumbers(recommendation.practiceMethod));
            setTarget(String(recommendation.masteryPlan.intervalsDays.length));
            setIntervals(recommendation.masteryPlan.intervalsDays.map(String));
          }}
        >
          おすすめを使う
        </button>
      </div>
      <form onSubmit={(event) => void save(event)}>
        <fieldset disabled={busy} className="drill-editor-fields">
          <label className="drill-field">
            <FieldLabel required>1回の練習方法</FieldLabel>
            <select
              value={methodType}
              onChange={(event) => {
                const type = event.target.value as PracticeMethod["type"];
                setMethodType(type);
                setNumbers(methodNumbers(methodDefaults[type]));
              }}
            >
              {Object.entries(methodNames).map(([type, name]) => (
                <option key={type} value={type}>
                  {name}
                </option>
              ))}
            </select>
          </label>
          <div className="drill-method-numbers">
            {Object.keys(numbers).map((key) => (
              <label className="drill-field" key={`${methodType}-${key}`}>
                <FieldLabel required>
                  {key === "minutes"
                    ? "練習時間（分）"
                    : key === "attempts"
                      ? "挑戦回数（回）"
                      : methodType === "consecutive"
                        ? "連続成功回数（回）"
                        : "成功回数（回）"}
                </FieldLabel>
                <input
                  type="number"
                  inputMode="numeric"
                  required
                  min={1}
                  max={key === "minutes" ? 300 : 1000}
                  step={1}
                  value={numbers[key]}
                  onChange={(event) =>
                    setNumbers({ ...numbers, [key]: event.target.value })
                  }
                />
              </label>
            ))}
          </div>
          <p>{methodHelp[methodType]}</p>
          <label className="drill-field drill-mastery-target">
            <FieldLabel required>マスターまでの達成目標回数</FieldLabel>
            <input
              type="number"
              inputMode="numeric"
              required
              min={1}
              max={1000}
              step={1}
              value={target}
              onChange={(event) => {
                const value = event.target.value;
                setTarget(value);
                const count = Number(value);
                if (Number.isInteger(count) && count >= 1 && count <= 1000)
                  setIntervals((current) =>
                    Array.from(
                      { length: count },
                      (_, index) =>
                        current[index] ??
                        String(defaultMasteryIntervalDays(index) ?? ""),
                    ),
                  );
              }}
            />
          </label>
          <div className="drill-mastery-interval-heading">
            <strong>取り組む間隔</strong>
            <span>前回の達成日から</span>
          </div>
          <ol className="drill-mastery-intervals">
            {intervals.map((days, index) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: milestones are identified by ordinal.
              <li key={index}>
                <span className="drill-milestone-number">{index + 1}</span>
                <span>{index + 1}回目</span>
                {index === 0 ? (
                  <strong>当日</strong>
                ) : (
                  <label>
                    <span className="drill-sr-only">
                      {index + 1}回目の間隔（日）
                    </span>
                    <input
                      type="number"
                      inputMode="numeric"
                      required
                      min={0}
                      max={maximumMasteryIntervalDays}
                      step={1}
                      value={days}
                      onChange={(event) =>
                        setIntervals((current) =>
                          current.map((value, position) =>
                            position === index ? event.target.value : value,
                          ),
                        )
                      }
                    />
                    <span>日後</span>
                  </label>
                )}
              </li>
            ))}
          </ol>
          <small>
            早めの達成も数えます。次の目安は実際の達成日から計算します。
          </small>
        </fieldset>
        {error && <DrillError message={error} />}
        <div className="drill-form-actions">
          <button
            type="button"
            className="secondary-button"
            disabled={busy}
            onClick={onCancel}
          >
            キャンセル
          </button>
          <button type="submit" className="primary-button" disabled={busy}>
            {busy
              ? "保存中…"
              : startOnSave
                ? "この方法で練習を始める"
                : "練習方法を保存"}
          </button>
        </div>
      </form>
    </section>
  );
}
